import React from "react";
import * as yup from "yup";
import { emailValidator } from "utils/fieldValidators";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import { pick } from "lodash";
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
} from "react-share";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import {
  fetchOwners,
  getCompanyInfo,
  referCompany,
} from "actions/employerActions";

import { toast } from "react-toastify";
import Button from "components/Button";
import ClipboardCopy from "components/ClipboardCopy";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import TermsModal from "pages/signUp/TermsModal";
import type { RootState } from "types/state";

import "./ReferralForm.scss";
import { ScrollToFieldError } from "utils/form";

const schema = yup.object({
  email: emailValidator("You must enter a valid email address")
    .label("Email")
    .required(),
});

interface ReferralFormValues {
  email: string;
}

/** A single beneficial owner row, narrowed to the fields `pick`ed below. */
interface OwnerResult {
  role: string;
  memberProfile?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

const actions = [employerConstants.REFER_COMPANY];
const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  // company is Record<string, unknown> in RootState; narrow at the boundary.
  const referralId = state.employer.company.referralId as string;
  return {
    activeCompany: state.employer.company,
    referralId,

    isLoading: loadingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  referCompany,
  getCompanyInfo,
  fetchOwners,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface ReferralFormState {
  showTermsOfService: boolean;
  fetchingOwners: boolean;
  theController: Partial<OwnerResult> | Record<string, never>;
  owners?: OwnerResult[];
  // read in render() but never set anywhere — preserved quirk.
  showAcctAgreement?: boolean;
}

class ReferralForm extends React.PureComponent<Props, ReferralFormState> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      showTermsOfService: false,
      fetchingOwners: true,
      theController: {},
    };
  }

  _onSubmit = (values: ReferralFormValues) => {
    void this.props
      .referCompany(this.props.client as ApolloClient<object>, values.email)
      .then((result) => {
        if (result.success) {
          toast.success("Referral Sent.");
          this.props.push("/dashboard");
        } else {
          toast.error("There was a problem sending referral.");
        }
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- preserved: original lifecycle is async; React ignores the returned promise (fire-and-forget)
  async componentDidMount() {
    window.analytics.page("Employer Terms");

    void this.props.getCompanyInfo(this.props.client as ApolloClient<object>);

    const { data: owners } = (await this.props.fetchOwners(
      this.props.client as ApolloClient<object>
    )) as { data: OwnerResult[] };

    this.setState({ fetchingOwners: false, owners });

    // eslint-disable-next-line no-var -- preserved quirk: original uses `var`
    var results = owners.map((item) =>
      pick(item, [
        "role",
        "memberProfile.firstName",
        "memberProfile.lastName",
        "memberProfile.email",
      ])
    );

    const theController = results.filter((person) =>
      // @ts-expect-error preserved quirk: `pick` yields Partial<OwnerResult> so
      // `role` is optional, but the original calls `.startsWith` unconditionally.
      person.role.startsWith("ControllingOfficer")
    );

    this.setState({ theController: theController[0] });

    this._isMounted = true;
  }

  _viewTerms = () => {
    return (
      <>
        <div className="terms">
          For every new company that joins Icon using your referral link, you
          both earn a credit.
          <a
            className="terms-link"
            onClick={() => {
              this.setState({
                showTermsOfService: true,
              });
            }}
            style={{
              display: "flex",
              color: "#01a3b0",
              cursor: "pointer",
            }}
          >
            <p className="terms-link-text"> Referral Program Agreements.</p>
          </a>
        </div>
      </>
    );
  };

  render() {
    const referralUrl = `https://referrals.iconsavingsplan.com?referralCode=${this.props.referralId}`;
    const terms = this._viewTerms();

    return (
      <div className="referral-form">
        {this.state.showTermsOfService && (
          <TermsModal
            termsInfo={this.state.theController}
            type="employerPartnerProgram"
            show={this.state.showTermsOfService}
            onClose={() => this.setState({ showTermsOfService: false })}
          />
        )}

        {!this.state.showAcctAgreement && (
          <Card>
            <Card.Title className="referral-title">
              <IconHeader variant="cardHeader" headerText="Refer & Earn" />
              <IconSubheader subheader={terms} />
            </Card.Title>
            <Card.Body className="referral-body">
              <Row className="copy-and-share">
                <Col>
                  <IconHeader
                    variant="referralHeader"
                    headerText="Copy and share your link"
                  />
                  <div className="referral-copy-share">
                    <span className="referral-box">
                      <p className="referral-link">{referralUrl}</p>
                    </span>
                    <div className="copy-link">
                      <ClipboardCopy copyText={referralUrl} />
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className="invite-email">
                <Col>
                  <IconHeader
                    variant="referralHeader"
                    headerText="Invite by email"
                  />
                  <Formik
                    validateOnChange={false}
                    validationSchema={schema}
                    onSubmit={(values) => {
                      this._onSubmit(values);
                    }}
                    enableReinitialize={true}
                    initialValues={{
                      email: "",
                    }}
                  >
                    {({
                      handleSubmit,
                      handleChange,
                      handleBlur,
                      values,
                      touched,
                      errors,
                    }) => (
                      <Form noValidate onSubmit={handleSubmit}>
                        <ScrollToFieldError />
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={9}
                            controlId="formBasicEmail"
                            style={{ paddingBottom: 10 }}
                          >
                            <Form.Label
                              style={{ color: "858C9C", paddingTop: 20 }}
                            >
                              Email Address
                            </Form.Label>

                            <div className="referral-btn">
                              <Form.Control
                                type="email"
                                name="email"
                                placeholder="referafriend@gmail.com"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.email && !!errors.email}
                                isValid={touched.email && !errors.email}
                              />
                              <span style={{ paddingLeft: 10, paddingTop: 3 }}>
                                <Button
                                  btnLabel="Send Invite"
                                  name="submit"
                                  withArrow={true}
                                  loading={this.props.isLoading}
                                />
                              </span>
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {errors.email}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                      </Form>
                    )}
                  </Formik>
                </Col>
              </Row>

              <Row>
                <Col>
                  <IconHeader
                    variant="referralHeader"
                    headerText="Share on socials"
                  />
                  <div className="share-social">
                    Share the link on:
                    <LinkedinShareButton
                      url={referralUrl}
                      title="Join Icon Savings Plan!"
                      summary="Use this referral link to sign up for Icon Savings Plan!"
                      source={referralUrl}
                      beforeOnClick={() => {
                        window.analytics.track("Shared on LinkedIn");
                      }}
                    >
                      <LinkedinIcon size={20} round />
                    </LinkedinShareButton>
                    |
                    <TwitterShareButton
                      url={referralUrl}
                      title="Use this referral link to sign up for Icon Savings Plan!"
                      hashtags={["IconSavingsPlan"]}
                      beforeOnClick={() => {
                        window.analytics.track("Shared on Twitter");
                      }}
                    >
                      <TwitterIcon size={20} round />
                    </TwitterShareButton>
                    |
                    <FacebookShareButton
                      url={referralUrl}
                      // @ts-expect-error preserved quirk: lib prop is `hashtag` (singular); original passes `hashtags`
                      quote="test"
                      hashtags="IconSavingsPlan"
                      beforeOnClick={() => {
                        window.analytics.track("Shared on Facebook");
                      }}
                    >
                      <FacebookIcon size={20} round />
                    </FacebookShareButton>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  }
}

export default connector(withApollo<Props>(ReferralForm));
